import React, { useState, useEffect } from 'react';
import { Plus, X, Calendar, Loader2, Phone, Mail, FileText, MessageSquare, Users, Clock, AlertTriangle, ChevronDown, ChevronUp, Trash2, Send } from 'lucide-react';
import { pipelineAPI, activityAPI, authAPI, salespersonAPI } from '../../services/api';
import { exportToCSV } from '../../utils/exportCSV';

const stages = [
  { id: 'Lead', color: '#9CA3AF', bg: '#F3F4F6' },
  { id: 'Qualified', color: '#3a6fc4', bg: '#e8eef7' },
  { id: 'Quoted', color: '#24549c', bg: '#dce6f5' },
  { id: 'Negotiation', color: '#E65100', bg: '#FFF3E0' },
  { id: 'Won', color: '#2E7D32', bg: '#E8F5E9' },
  { id: 'Lost', color: '#C62828', bg: '#FFEBEE' },
];

const activityTypes = [
  { id: 'call', label: 'Call', icon: Phone, color: '#2563eb' },
  { id: 'meeting', label: 'Meeting', icon: Users, color: '#7c3aed' },
  { id: 'email', label: 'Email', icon: Mail, color: '#059669' },
  { id: 'note', label: 'Note', icon: FileText, color: '#d97706' },
  { id: 'follow_up', label: 'Follow-up', icon: Clock, color: '#dc2626' },
];

const SalesPipeline = () => {
  const [leads, setLeads] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState({ pipeline_value: 0, won_value: 0, active_leads: 0 });
  const [salesmen, setSalesmen] = useState([]);
  const [newLead, setNewLead] = useState({
    customer: '', contact: '', phone: '', email: '', value: 0, products: '',
    salesperson_id: '', salesperson_name: '', branch: 'BR-01', next_follow_up: '', notes: ''
  });
  const [draggedId, setDraggedId] = useState(null);

  // Stage filter (Stock-style chips) + report-style date range for the list view
  const [stageFilter, setStageFilter] = useState(''); // '' = All (Kanban board); a stage id = table view
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Follow-up reminders
  const [followups, setFollowups] = useState({ overdue: [], upcoming: [], today: '' });
  const [showReminders, setShowReminders] = useState(true);

  // Activity log state
  const [activities, setActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityForm, setActivityForm] = useState({
    activity_type: 'call', title: '', description: '', contact_person: '', follow_up_date: '', duration_minutes: ''
  });
  const [activitySubmitting, setActivitySubmitting] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);

  useEffect(() => {
    fetchLeads();
    fetchStats();
    fetchFollowups();
    fetchSalesmen();
  }, []);

  const fetchSalesmen = async () => {
    try {
      const data = await salespersonAPI.getAll();
      setSalesmen(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const data = await pipelineAPI.getAll({ page_size: 2000 });
      setLeads(data);
    } catch (err) {
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await pipelineAPI.getStats();
      setStats(data);
    } catch (err) { console.error(err); }
  };

  const fetchFollowups = async () => {
    try {
      const data = await activityAPI.getUpcomingFollowups(7);
      setFollowups(data);
    } catch (err) { console.error(err); }
  };

  const fetchActivities = async (leadId) => {
    try {
      setActivityLoading(true);
      const data = await activityAPI.getForEntity('lead', leadId);
      setActivities(data.items || []);
    } catch (err) { console.error(err); }
    finally { setActivityLoading(false); }
  };

  const pipelineValue = stats.pipeline_value || 0;
  const wonValue = stats.won_value || 0;
  const activeLeads = stats.active_leads || 0;

  // --- Stage filter + date range (report-style list view) ---
  const salesmanName = (id) => salesmen.find(s => s.id === id)?.name || id || '-';
  const leadSalesperson = (l) => l.salesperson_name || salesmen.find(s => s.id === l.salesperson_id)?.name || salesmanName(l.salesman);

  const dateFilteredLeads = leads.filter(l => {
    if (!dateFrom && !dateTo) return true;
    const d = (l.created_at || '').toString().slice(0, 10);
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  });
  const stageCounts = stages.reduce((acc, s) => {
    acc[s.id] = dateFilteredLeads.filter(l => l.stage === s.id).length;
    return acc;
  }, {});
  const allCount = dateFilteredLeads.length;
  const tableLeads = stageFilter
    ? dateFilteredLeads.filter(l => l.stage === stageFilter).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    : [];

  const exportColumns = [
    { label: 'Customer', accessor: 'customer' },
    { label: 'Contact', accessor: 'contact' },
    { label: 'Phone', accessor: 'phone' },
    { label: 'Email', accessor: 'email' },
    { label: 'Products', accessor: 'products' },
    { label: 'Value', accessor: 'value' },
    { label: 'Probability %', accessor: 'probability' },
    { label: 'Salesman', accessor: (l) => leadSalesperson(l) },
    { label: 'Next Follow-up', accessor: 'next_follow_up' },
    { label: 'Stage', accessor: 'stage' },
  ];
  const handleExportList = () => {
    if (!tableLeads.length) return;
    exportToCSV(tableLeads, exportColumns, `pipeline_${stageFilter.toLowerCase()}`);
  };

  const handleDrop = async (stage) => {
    if (!draggedId) return;
    try {
      await pipelineAPI.update(draggedId, {
        stage,
        probability: stage === 'Won' ? 100 : stage === 'Lost' ? 0 : undefined
      });
      setLeads(leads.map(l => l.id === draggedId ? { ...l, stage, probability: stage === 'Won' ? 100 : stage === 'Lost' ? 0 : l.probability } : l));
      setDraggedId(null);
      fetchStats();
    } catch (err) {
      console.error(err);
      setDraggedId(null);
    }
  };

  const handleAdd = async () => {
    try {
      setSubmitting(true);
      const leadData = {
        ...newLead,
        value: Number(newLead.value),
        stage: 'Lead',
        probability: 20
      };
      const created = await pipelineAPI.create(leadData);
      setLeads([created, ...leads]);
      setShowAdd(false);
      setNewLead({ customer: '', contact: '', phone: '', email: '', value: 0, products: '', salesperson_id: '', salesperson_name: '', branch: 'BR-01', next_follow_up: '', notes: '' });
      fetchStats();
      fetchFollowups();
    } catch (err) {
      console.error(err);
      alert('Failed to create lead: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const openLeadDetail = (lead) => {
    setSelected(lead);
    setShowActivityForm(false);
    setActivityForm({ activity_type: 'call', title: '', description: '', contact_person: '', follow_up_date: '', duration_minutes: '' });
    fetchActivities(lead.id);
  };

  const handleLogActivity = async () => {
    if (!activityForm.title.trim()) return;
    try {
      setActivitySubmitting(true);
      await activityAPI.create({
        entity_type: 'lead',
        entity_id: selected.id,
        ...activityForm,
        duration_minutes: activityForm.duration_minutes ? Number(activityForm.duration_minutes) : null
      });
      setShowActivityForm(false);
      setActivityForm({ activity_type: 'call', title: '', description: '', contact_person: '', follow_up_date: '', duration_minutes: '' });
      fetchActivities(selected.id);
      // Refresh follow-ups if a follow-up date was set
      if (activityForm.follow_up_date) {
        fetchFollowups();
        fetchLeads();
      }
    } catch (err) {
      console.error(err);
      alert('Failed to log activity');
    } finally {
      setActivitySubmitting(false);
    }
  };

  const deleteActivity = async (actId) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await activityAPI.delete(actId);
      setActivities(activities.filter(a => a.id !== actId));
    } catch (err) { alert('Failed to delete'); }
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    return dateStr < followups.today;
  };

  const isDueToday = (dateStr) => {
    if (!dateStr) return false;
    return dateStr === followups.today;
  };

  const overdueCount = followups.overdue?.length || 0;
  const upcomingCount = followups.upcoming?.length || 0;

  const getActivityIcon = (type) => {
    const at = activityTypes.find(a => a.id === type);
    return at || activityTypes[3];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  return (
    <>
      {/* Follow-Up Reminders Bar */}
      {(overdueCount > 0 || upcomingCount > 0) && (
        <div className="mb-4 rounded-xl border overflow-hidden" data-testid="followup-reminders-bar">
          <button
            onClick={() => setShowReminders(!showReminders)}
            className={`w-full flex items-center justify-between px-4 py-3 text-xs font-semibold transition-colors ${
              overdueCount > 0 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200'
            }`}
            data-testid="followup-toggle-btn"
          >
            <div className="flex items-center gap-2">
              {overdueCount > 0 ? <AlertTriangle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
              <span>
                {overdueCount > 0 && <>{overdueCount} overdue follow-up{overdueCount > 1 ? 's' : ''}</>}
                {overdueCount > 0 && upcomingCount > 0 && ' + '}
                {upcomingCount > 0 && <>{upcomingCount} upcoming this week</>}
              </span>
            </div>
            {showReminders ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {showReminders && (
            <div className="bg-white p-3 space-y-2 max-h-48 overflow-y-auto">
              {followups.overdue?.map(lead => (
                <div key={lead.id}
                  onClick={() => openLeadDetail(lead)}
                  className="flex items-center justify-between p-2.5 rounded-lg bg-red-50/50 border border-red-100 cursor-pointer hover:bg-red-50 transition-colors"
                  data-testid={`overdue-lead-${lead.id}`}
                >
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-gray-900">{lead.customer}</p>
                      <p className="text-[10px] text-red-500">Due: {formatDate(lead.next_follow_up)}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-gray-600">{'\u20b9'}{(lead.value / 100000).toFixed(1)}L</span>
                </div>
              ))}
              {followups.upcoming?.map(lead => (
                <div key={lead.id}
                  onClick={() => openLeadDetail(lead)}
                  className="flex items-center justify-between p-2.5 rounded-lg bg-amber-50/50 border border-amber-100 cursor-pointer hover:bg-amber-50 transition-colors"
                  data-testid={`upcoming-lead-${lead.id}`}
                >
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3 text-amber-500 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-gray-900">{lead.customer}</p>
                      <p className="text-[10px] text-amber-600">Due: {formatDate(lead.next_follow_up)}</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-gray-600">{'\u20b9'}{(lead.value / 100000).toFixed(1)}L</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        {[
          { l: 'Pipeline Value', v: `\u20b9${(pipelineValue / 100000).toFixed(1)}L` },
          { l: 'Won This Month', v: `\u20b9${(wonValue / 100000).toFixed(1)}L` },
          { l: 'Active Leads', v: activeLeads }
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{s.v}</p>
            <p className="text-[10px] text-gray-500">{s.l}</p>
          </div>
        ))}
      </div>

      <div className="flex justify-end mb-4">
        <button onClick={() => setShowAdd(true)} data-testid="add-lead-btn"
          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg hover:shadow-blue-200 transition-all"
          style={{ backgroundColor: '#24549c' }}>
          <Plus className="w-3.5 h-3.5" /> Add Lead
        </button>
      </div>

      {/* Stage Filter Chips (Stock-style) */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <button
          data-testid="pipeline-stage-all"
          onClick={() => setStageFilter('')}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-full border transition-colors ${stageFilter === '' ? 'bg-[#24549c] text-white border-[#24549c]' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
        >
          All (Board)
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${stageFilter === '' ? 'bg-white/20' : 'bg-white'}`}>{allCount}</span>
        </button>
        {stages.map(s => (
          <button
            key={s.id}
            data-testid={`pipeline-stage-${s.id}`}
            onClick={() => setStageFilter(prev => prev === s.id ? '' : s.id)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-full border transition-colors"
            style={stageFilter === s.id ? { backgroundColor: s.color, color: '#fff', borderColor: s.color } : { backgroundColor: s.bg, color: s.color, borderColor: s.color + '55' }}
          >
            {s.id}
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={stageFilter === s.id ? { backgroundColor: 'rgba(255,255,255,0.25)' } : { backgroundColor: '#fff' }}>{stageCounts[s.id] || 0}</span>
          </button>
        ))}
        {stageFilter && (
          <button onClick={() => { setStageFilter(''); setDateFrom(''); setDateTo(''); }} className="text-[10px] text-gray-500 hover:text-gray-700 underline ml-1" data-testid="pipeline-clear-filter">Clear filter</button>
        )}
      </div>

      {/* Date range + export (report-style, shown for the list view) */}
      {stageFilter && (
        <div className="flex items-center gap-3 flex-wrap mb-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
          <span className="text-[11px] font-semibold text-gray-600">Created between</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="pipeline-date-from"
            className="px-2.5 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
          <span className="text-[11px] text-gray-400">to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="pipeline-date-to"
            className="px-2.5 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-[10px] text-gray-500 hover:text-gray-700 underline">Clear dates</button>
          )}
          <div className="ml-auto flex items-center gap-3">
            <span className="text-[11px] font-semibold text-gray-500">{tableLeads.length} lead{tableLeads.length !== 1 ? 's' : ''}</span>
            <button onClick={handleExportList} disabled={!tableLeads.length} data-testid="pipeline-export-csv"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-white rounded-lg disabled:opacity-40" style={{ backgroundColor: '#24549c' }}>
              <FileText className="w-3 h-3" /> Export CSV
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-sm text-gray-500">Loading pipeline...</span>
        </div>
      ) : stageFilter ? (
        /* ---- Stage LIST view (like Stock filter results) ---- */
        <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto" data-testid="pipeline-stage-table">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="text-[10px] font-bold text-gray-500 uppercase" style={{ backgroundColor: '#f8f9fb' }}>
                {['Customer', 'Contact', 'Phone', 'Email', 'Products', 'Value', 'Prob %', 'Salesman', 'Next Follow-up', 'Stage'].map(h => (
                  <th key={h} className="text-left px-3 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableLeads.map(lead => (
                <tr key={lead.id} onClick={() => openLeadDetail(lead)} data-testid={`pipeline-row-${lead.id}`}
                  className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer">
                  <td className="px-3 py-2.5 text-xs font-semibold text-gray-900 whitespace-nowrap">{lead.customer}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{lead.contact || '-'}</td>
                  <td className="px-3 py-2.5 text-xs text-blue-600 whitespace-nowrap">{lead.phone || '-'}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{lead.email || '-'}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-600 max-w-[200px] truncate" title={lead.products}>{lead.products || '-'}</td>
                  <td className="px-3 py-2.5 text-xs font-bold whitespace-nowrap" style={{ color: '#24549c' }}>{'\u20b9'}{(lead.value || 0).toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">{lead.probability}%</td>
                  <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{leadSalesperson(lead)}</td>
                  <td className={`px-3 py-2.5 text-xs whitespace-nowrap ${isOverdue(lead.next_follow_up) ? 'text-red-500 font-semibold' : 'text-gray-600'}`}>
                    {lead.next_follow_up ? formatDate(lead.next_follow_up) : '-'}{isOverdue(lead.next_follow_up) && ' (Overdue)'}
                  </td>
                  <td className="px-3 py-2.5"><span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: (stages.find(s => s.id === lead.stage) || {}).bg, color: (stages.find(s => s.id === lead.stage) || {}).color }}>{lead.stage}</span></td>
                </tr>
              ))}
              {tableLeads.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-10 text-center text-xs text-gray-400">No {stageFilter} leads{(dateFrom || dateTo) ? ' in this date range' : ''}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: '450px' }}>
          {stages.map(stage => {
            const stageLeads = leads.filter(l => l.stage === stage.id);
            return (
              <div key={stage.id}
                className="flex-shrink-0 w-60 bg-gray-50 rounded-xl p-3"
                onDragOver={e => e.preventDefault()}
                onDrop={() => handleDrop(stage.id)}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                    <span className="text-xs font-bold text-gray-700">{stage.id}</span>
                  </div>
                  <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: stage.bg, color: stage.color }}>{stageLeads.length}</span>
                </div>
                <div className="space-y-2">
                  {stageLeads.map(lead => (
                    <div key={lead.id}
                      draggable
                      onDragStart={() => setDraggedId(lead.id)}
                      onClick={() => openLeadDetail(lead)}
                      className="bg-white rounded-lg p-3 border border-gray-100 cursor-grab hover:shadow-md transition-all active:cursor-grabbing"
                      data-testid={`lead-card-${lead.id}`}
                    >
                      <p className="text-xs font-bold text-gray-900 mb-1 truncate">{lead.customer}</p>
                      <p className="text-[10px] text-gray-500 mb-2 truncate">{lead.products}</p>
                      {(lead.phone || lead.email) && (
                        <div className="flex items-center gap-2 mb-2">
                          {lead.phone && (
                            <a href={`tel:${lead.phone}`} onClick={e => e.stopPropagation()}
                              className="flex items-center gap-1 text-[9px] text-blue-600 hover:text-blue-800" title={lead.phone}>
                              <Phone className="w-2.5 h-2.5" /><span className="truncate max-w-[60px]">{lead.phone}</span>
                            </a>
                          )}
                          {lead.email && (
                            <a href={`mailto:${lead.email}`} onClick={e => e.stopPropagation()}
                              className="flex items-center gap-1 text-[9px] text-green-600 hover:text-green-800" title={lead.email}>
                              <Mail className="w-2.5 h-2.5" /><span className="truncate max-w-[60px]">{lead.email}</span>
                            </a>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold" style={{ color: '#24549c' }}>{'\u20b9'}{(lead.value / 100000).toFixed(1)}L</span>
                        <span className="text-[9px] text-gray-400">{lead.probability}%</span>
                      </div>
                      {lead.next_follow_up && (
                        <p className={`text-[9px] mt-1 flex items-center gap-1 ${
                          isOverdue(lead.next_follow_up) ? 'text-red-500 font-semibold' :
                          isDueToday(lead.next_follow_up) ? 'text-amber-500 font-semibold' : 'text-gray-400'
                        }`}>
                          {isOverdue(lead.next_follow_up) ? <AlertTriangle className="w-2.5 h-2.5" /> : <Calendar className="w-2.5 h-2.5" />}
                          {formatDate(lead.next_follow_up)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lead Detail + Activity Log Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="lead-detail-modal">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
              <div>
                <h2 className="text-base font-bold" style={{ fontFamily: 'DM Sans' }}>{selected.customer}</h2>
                <p className="text-[11px] text-gray-500 mt-0.5">{selected.products}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-4">
              {/* Lead Details */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-gray-500">Contact</span><p className="font-semibold">{selected.contact}</p></div>
                <div><span className="text-gray-500">Value</span><p className="font-bold" style={{ color: '#24549c' }}>{'\u20b9'}{selected.value?.toLocaleString()}</p></div>
                <div>
                  <span className="text-gray-500">Phone</span>
                  {selected.phone ? (
                    <a href={`tel:${selected.phone}`} className="flex items-center gap-1.5 font-semibold text-blue-700 hover:text-blue-800" data-testid="lead-phone-link">
                      <Phone className="w-3 h-3" />{selected.phone}
                    </a>
                  ) : (
                    <p className="text-gray-400 italic">Not added</p>
                  )}
                </div>
                <div>
                  <span className="text-gray-500">Email</span>
                  {selected.email ? (
                    <a href={`mailto:${selected.email}`} className="flex items-center gap-1.5 font-semibold text-blue-700 hover:text-blue-800" data-testid="lead-email-link">
                      <Mail className="w-3 h-3" />{selected.email}
                    </a>
                  ) : (
                    <p className="text-gray-400 italic">Not added</p>
                  )}
                </div>
                <div><span className="text-gray-500">Stage</span><p className="font-semibold">{selected.stage} ({selected.probability}%)</p></div>
                <div>
                  <span className="text-gray-500">Next Follow-up</span>
                  <p className={`font-semibold ${isOverdue(selected.next_follow_up) ? 'text-red-500' : ''}`}>
                    {selected.next_follow_up ? formatDate(selected.next_follow_up) : 'Not set'}
                    {isOverdue(selected.next_follow_up) && ' (Overdue)'}
                  </p>
                </div>
              </div>
              {selected.notes && <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-600">{selected.notes}</div>}

              {/* Move Stage */}
              <div>
                <p className="text-[10px] text-gray-500 mb-2">Move Stage</p>
                <div className="flex gap-2 flex-wrap">
                  {stages.map(s => (
                    <button key={s.id}
                      onClick={async () => {
                        try {
                          await pipelineAPI.update(selected.id, { stage: s.id });
                          setLeads(leads.map(l => l.id === selected.id ? { ...l, stage: s.id } : l));
                          setSelected({ ...selected, stage: s.id });
                          fetchStats();
                        } catch (err) { console.error(err); }
                      }}
                      className={`px-2.5 py-1 text-[10px] font-semibold rounded-full transition-all ${selected.stage === s.id ? 'text-white' : ''}`}
                      style={selected.stage === s.id ? { backgroundColor: s.color } : { backgroundColor: s.bg, color: s.color }}
                    >{s.id}</button>
                  ))}
                </div>
              </div>

              {/* Activity Log Section */}
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold text-gray-900 flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5" style={{ color: '#24549c' }} />
                    Activity Log
                  </h3>
                  <button
                    onClick={() => setShowActivityForm(!showActivityForm)}
                    data-testid="log-activity-btn"
                    className="flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold text-white rounded-lg transition-all"
                    style={{ backgroundColor: '#24549c' }}
                  >
                    <Plus className="w-3 h-3" /> Log Activity
                  </button>
                </div>

                {/* Activity Form */}
                {showActivityForm && (
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 mb-3 space-y-2.5" data-testid="activity-form">
                    {/* Activity Type Selector */}
                    <div className="flex gap-1.5">
                      {activityTypes.map(at => {
                        const Icon = at.icon;
                        return (
                          <button key={at.id}
                            onClick={() => setActivityForm({ ...activityForm, activity_type: at.id })}
                            className={`flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-semibold rounded-lg transition-all ${
                              activityForm.activity_type === at.id ? 'text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-100'
                            }`}
                            style={activityForm.activity_type === at.id ? { backgroundColor: at.color } : {}}
                            data-testid={`activity-type-${at.id}`}
                          >
                            <Icon className="w-3 h-3" />{at.label}
                          </button>
                        );
                      })}
                    </div>
                    <input
                      value={activityForm.title}
                      onChange={e => setActivityForm({ ...activityForm, title: e.target.value })}
                      placeholder="Activity title (e.g., 'Discussed pricing with Rajesh')"
                      data-testid="activity-title-input"
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
                    />
                    <textarea
                      value={activityForm.description}
                      onChange={e => setActivityForm({ ...activityForm, description: e.target.value })}
                      placeholder="Details / notes..."
                      rows={2}
                      data-testid="activity-desc-input"
                      className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        value={activityForm.contact_person}
                        onChange={e => setActivityForm({ ...activityForm, contact_person: e.target.value })}
                        placeholder="Contact person"
                        data-testid="activity-contact-input"
                        className="px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
                      />
                      <input
                        type="date"
                        value={activityForm.follow_up_date}
                        onChange={e => setActivityForm({ ...activityForm, follow_up_date: e.target.value })}
                        data-testid="activity-followup-input"
                        className="px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
                        title="Schedule next follow-up"
                      />
                      <input
                        type="number"
                        value={activityForm.duration_minutes}
                        onChange={e => setActivityForm({ ...activityForm, duration_minutes: e.target.value })}
                        placeholder="Duration (min)"
                        data-testid="activity-duration-input"
                        className="px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setShowActivityForm(false)} className="px-3 py-1.5 text-[10px] font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
                      <button
                        onClick={handleLogActivity}
                        disabled={activitySubmitting || !activityForm.title.trim()}
                        data-testid="submit-activity-btn"
                        className="px-3 py-1.5 text-[10px] font-semibold text-white rounded-lg disabled:opacity-50 flex items-center gap-1"
                        style={{ backgroundColor: '#24549c' }}
                      >
                        {activitySubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        Save
                      </button>
                    </div>
                  </div>
                )}

                {/* Activities List */}
                {activityLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    <span className="ml-1.5 text-[10px] text-gray-400">Loading activities...</span>
                  </div>
                ) : activities.length === 0 ? (
                  <div className="text-center py-6">
                    <MessageSquare className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-[10px] text-gray-400">No activities logged yet</p>
                    <p className="text-[9px] text-gray-300 mt-0.5">Click "Log Activity" to record a call, meeting, or note</p>
                  </div>
                ) : (
                  <div className="space-y-0 relative">
                    {/* Timeline line */}
                    <div className="absolute left-[11px] top-3 bottom-3 w-px bg-gray-200" />
                    {activities.map((act, idx) => {
                      const at = getActivityIcon(act.activity_type);
                      const Icon = at.icon;
                      return (
                        <div key={act.id} className="flex gap-3 relative group" data-testid={`activity-item-${act.id}`}>
                          <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center z-10"
                            style={{ backgroundColor: at.color + '18' }}>
                            <Icon className="w-3 h-3" style={{ color: at.color }} />
                          </div>
                          <div className="flex-1 pb-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="text-xs font-semibold text-gray-900">{act.title}</p>
                                {act.description && <p className="text-[10px] text-gray-500 mt-0.5">{act.description}</p>}
                              </div>
                              <button
                                onClick={() => deleteActivity(act.id)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-gray-300 hover:text-red-400 transition-all"
                                data-testid={`delete-activity-${act.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-[9px] text-gray-400">
                              <span>{formatDate(act.created_at)} {formatTime(act.created_at)}</span>
                              {act.created_by_name && <span>by {act.created_by_name}</span>}
                              {act.contact_person && <span>with {act.contact_person}</span>}
                              {act.duration_minutes && <span>{act.duration_minutes} min</span>}
                              {act.follow_up_date && (
                                <span className="text-amber-500 font-semibold flex items-center gap-0.5">
                                  <Clock className="w-2.5 h-2.5" /> Follow-up: {formatDate(act.follow_up_date)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end p-4 border-t border-gray-100 flex-shrink-0">
              <button onClick={() => setSelected(null)} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Lead Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-base font-bold" style={{ fontFamily: 'DM Sans' }}>New Lead</h2>
              <button onClick={() => setShowAdd(false)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Customer/Company</label>
                  <input value={newLead.customer} onChange={e => setNewLead({ ...newLead, customer: e.target.value })}
                    data-testid="new-lead-customer"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Contact Person</label>
                  <input value={newLead.contact} onChange={e => setNewLead({ ...newLead, contact: e.target.value })}
                    data-testid="new-lead-contact"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input value={newLead.phone} onChange={e => setNewLead({ ...newLead, phone: e.target.value })}
                      data-testid="new-lead-phone" placeholder="e.g. 9876543210"
                      className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input type="email" value={newLead.email} onChange={e => setNewLead({ ...newLead, email: e.target.value })}
                      data-testid="new-lead-email" placeholder="e.g. contact@company.com"
                      className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Products Required</label>
                <input value={newLead.products} onChange={e => setNewLead({ ...newLead, products: e.target.value })}
                  data-testid="new-lead-products"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Estimated Value ({'\u20b9'})</label>
                  <input type="number" value={newLead.value} onChange={e => setNewLead({ ...newLead, value: e.target.value })}
                    data-testid="new-lead-value"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Salesman</label>
                  <select value={newLead.salesperson_id}
                    onChange={e => {
                      const sp = salesmen.find(s => s.id === e.target.value);
                      setNewLead({ ...newLead, salesperson_id: e.target.value, salesperson_name: sp?.name || '' });
                    }}
                    data-testid="new-lead-salesman"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200">
                    <option value="">Select</option>
                    {salesmen.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Follow-up Date</label>
                  <input type="date" value={newLead.next_follow_up} onChange={e => setNewLead({ ...newLead, next_follow_up: e.target.value })}
                    data-testid="new-lead-followup"
                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={newLead.notes} onChange={e => setNewLead({ ...newLead, notes: e.target.value })} rows={2}
                  data-testid="new-lead-notes"
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setShowAdd(false)} disabled={submitting} className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50">Cancel</button>
              <button onClick={handleAdd} disabled={submitting} data-testid="submit-new-lead-btn"
                className="px-4 py-2 text-xs font-semibold text-white rounded-lg hover:shadow-lg hover:shadow-blue-200 disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: '#24549c' }}>
                {submitting ? <><Loader2 className="w-3 h-3 animate-spin" /> Adding...</> : 'Add Lead'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SalesPipeline;
